import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart, Users, Home } from 'lucide-react';
function CountUp({
  end,
  suffix = '',
  duration = 2
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number;
      let animationFrame: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / (duration * 1000);
        if (progress < 1) {
          setCount(Math.floor(end * progress));
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };
      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [end, duration, isInView]);
  return <span ref={ref} data-id="element-226" data-mp="1wfjsiw5didhm">
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export function Impact() {
  const stats = [{
    icon: <Heart className="w-6 h-6 text-midnight" data-id="element-227" data-mp="21f64z3ddnppd" />,
    number: 1700,
    suffix: '+',
    label: 'Children Reached',
    description: 'Across 13 orphanages through dedicated outreach programs.',
    color: 'bg-gold',
    textColor: 'text-midnight'
  }, {
    icon: <Users className="w-6 h-6 text-ivory" data-id="element-228" data-mp="2fcug3jhn7ih8" />,
    number: 2700,
    suffix: '+',
    label: 'Inmates Ministered To',
    description: 'Bringing hope and the word to correctional centers.',
    color: 'bg-midnight-light',
    textColor: 'text-ivory'
  }, {
    icon: <Home className="w-6 h-6 text-midnight" data-id="element-229" data-mp="2u0pbj7n1c0y0" />,
    number: 800,
    suffix: '+',
    label: 'Families Touched',
    description: 'Supported and prayed for via our village outreaches.',
    color: 'bg-white',
    textColor: 'text-midnight'
  }];
  return <section id="impact" className="py-32 bg-midnight text-ivory" data-id="element-230" data-mp="13x5inak69eu9">
      <div className="max-w-7xl mx-auto px-6" data-id="element-231" data-mp="yyu8eypyt2bi">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16" data-id="element-232" data-mp="z05amu3ri20i">
          <div data-id="element-233" data-mp="1crad03yil8ej">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-xs font-bold tracking-widest uppercase mb-6 text-gold" data-id="element-234" data-mp="1crtkeqqjgeeb">
              Chapter 01 — Reach
            </motion.div>
            <motion.h3 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-5xl md:text-7xl font-display font-bold tracking-tighter" data-id="element-235" data-mp="1roavrhyq9t0v">
              Seven years. <br data-id="element-236" data-mp="39k386kl0jb1g" />
              <span className="font-serif italic font-medium text-gold" data-id="element-237" data-mp="39kmojwv0lrdd">
                Countless lives.
              </span>
            </motion.h3>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6" data-id="element-238" data-mp="2zlx4pyjk5wuh">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} className={`${stat.color} ${stat.textColor} p-8 md:p-10 rounded-[2rem] flex flex-col justify-between group hover:-translate-y-2 transition-transform duration-300`} data-id="element-239" data-mp="34n4do0xft22q" data-mpi={index}>
              <div className="flex items-center justify-between mb-12" data-id="element-240" data-mp="2kzd4iy6yfrla">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${stat.textColor === 'text-ivory' ? 'bg-white/10' : 'bg-midnight/5'}`} data-id="element-241" data-mp="2fy7bt0dao4br">
                  {stat.icon}
                </div>
                <span className="text-sm font-bold tracking-widest uppercase opacity-50" data-id="element-242" data-mp="270dh1tv6hiqp">
                  0{index + 1}
                </span>
              </div>

              <div data-id="element-243" data-mp="1x9is2hqjs51k">
                <div className="text-6xl md:text-7xl font-display font-black tracking-tighter mb-4" data-id="element-244" data-mp="1x90m2te32cgg">
                  <CountUp end={stat.number} suffix={stat.suffix} data-id="element-245" data-mp="1sb7k6cjtc4lz" />
                </div>
                <h4 className="text-xl font-bold mb-3" data-id="element-246" data-mp="1o79k9zmnyprv">{stat.label}</h4>
                <p className={`font-medium leading-relaxed ${stat.textColor === 'text-ivory' ? 'text-ivory/70' : 'text-midnight/70'}`} data-id="element-247" data-mp="1j8g41zfzsko0">
                  {stat.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}